import { useEffect } from "react";
import { onSubscriptionDelivery } from "utils/subscriptionLiveness";

type Params = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	shouldSkip: boolean;
	setIsSubscriptionBroken: (broken: boolean) => void;
};

/**
 * Recovery counterpart to the liveness watchdog. While the stream is marked broken but the network is up
 * (and we are not intentionally skipped), the first DELIVERY or RE-REGISTRATION frame after a reconnect —
 * a data frame, start_ack, or connection_ack (NOT a bare ka keepalive, which only proves the socket is
 * warm, not that this subscription is delivering) — proves delivery has resumed, so clear the broken flag.
 * This stops an idle fridge (which may never send a data frame) from sitting on a stale broken flag after a
 * merely transient blip and triggering the reload backstop. Event-driven and passive: it reacts to
 * observed inbound traffic; it never polls or sends. Offline is excluded — the DNS-probe lane owns that.
 */
export const useClearBrokenOnInbound = ({
	isSubscriptionBroken,
	appConnected,
	shouldSkip,
	setIsSubscriptionBroken,
}: Params): void => {
	useEffect(() => {
		if (!(isSubscriptionBroken && appConnected) || shouldSkip) {
			return;
		}
		const unsubscribe = onSubscriptionDelivery(() => {
			setIsSubscriptionBroken(false);
		});
		return unsubscribe;
	}, [isSubscriptionBroken, appConnected, shouldSkip, setIsSubscriptionBroken]);
};
